import React from 'react'
import PaperBase from '../../../../../Paper/PaperBase'
import GranularStepCard from '../../../../../card/GranularStepCard'
import { DetailMarking } from '../DetailMarking/DetailMarking'
import { Tooltip } from '@mui/material'
import { ContentTooltipMarkingInfo, useStyles } from '../ContentTooltip/ContentTooltip'
import MarkingUtils from '../../../../../../utils/marking.utils'
import IconTooltipButton from '../../../../../button/IconTooltipButton'

const CardGranularStep = ({
  lcs = [],
  levelId,
  level,
  nextLevel,
  maxLevel,

  onSetMarking,
  onContextMenu,
  completed,

  marking,
  minMarking,
  maxMarking,

  disabled,
  onWorkedExamples,
  onEvidenceOfLearning,
  onNote,
  isBulk
}) => {
  const classes = useStyles()
  if (lcs.every((x) => x.granularSteps)) {
    let lcgs = []
    lcs.forEach((lc) => {
      const granularSteps = lc.granularSteps
        ?.filter((step) => step.levelId === levelId)
        .map((_, i) => String(i))
      lcgs = [...lcgs, ...granularSteps]
    })
    lcgs = [...new Set(lcgs)]

    const items = lcgs
    if (items.length > 0) {
      return items
        .map((item, index) => {
          const isLevel = index === 0
          if (!isLevel && !nextLevel) {
            return null
          }

          const colors = MarkingUtils.getColorLabel(marking, maxLevel, isLevel, level)
          return (
            <div key={`granular-steps-${levelId}-gs-${item}`} className='flex flex-nowrap w-full gap-1'>
              <PaperBase
                backgroundColor={colors.backgroundColor}
                color={colors.color}
                className='font-bold min-w-100px max-w-100px sticky left-0 z-10'
                outline={isLevel ? null : `1px solid ${colors?.outline}`}
                children={isLevel ? level.name : `Working towards ${nextLevel?.name}`}
              />
              {lcs.map((lc, ilc) => {
                const gsslc = lc.granularSteps
                  ?.filter((step) => step.levelId === levelId)
                  ?.map((item) => ({
                    ...item,
                    position: parseInt(item.id.split('gs-')[1])
                  }))
                  ?.sort((a, b) => a.position - b.position)

                const find = gsslc[index]
                const deff = find?.definition

                let findMarking = marking.find((item) => item._id === find?.[completed])
                if (!findMarking) {
                  findMarking = minMarking
                }
                const markingLevel = {
                  lcId: lc._id,
                  levelId,
                  gsId: find?.id,
                  id: find?.id,
                  isLevel: find?.isLevel,
                  completed: find?.[completed]
                    ? find[completed] === minMarking._id
                      ? maxMarking._id
                      : minMarking._id
                    : maxMarking._id,
                  code: index
                }
                const isClickeabled = Boolean(find?.definition && onSetMarking && !disabled && lc?.ulId)
                const log = find?.logs?.find(
                  (log) =>
                    log.markingType === completed &&
                    log.exists &&
                    log.marking !== minMarking._id &&
                    log.definition
                )

                if (log) {
                  find.definition = (
                    <Tooltip
                      id={`granular-steps-${levelId}-gs-${item}-lc-${lc._id}-current-marking-${log._id}`}
                      arrow
                      placement='top'
                      title={
                        <div className='py-1'>
                          <ContentTooltipMarkingInfo marking={log} />
                        </div>
                      }
                      classes={{ tooltip: classes.customWidth }}
                    >
                      <span className='text-ellipsis text-sm cursor-pointer'>{deff}</span>
                    </Tooltip>
                  )
                }

                const id = `granular-steps-${levelId}-gs-${item}-lc-${lc._id}-ul-${lc.ulId || 'main'}-class-${lc.classId || 'main'}-index-${ilc}`
                return (
                  <GranularStepCard
                    id={id}
                    key={id}
                    backgroundColor={find ? findMarking?.primaryColor : null}
                    color={find ? findMarking?.secondaryColor : null}
                    step={{ ...find }}
                    className={[
                      isClickeabled ? 'cursor-pointer hover-outline-muted' : '',
                      !lc?.ulId || disabled ? 'opacity-50' : ''
                    ].join(' ')}
                    onContextMenu={
                      isClickeabled
                        ? (e) =>
                            onContextMenu(e, {
                              markingLevel,
                              granularSteps: lc.granularSteps,
                              lcId: lc._id,
                              gsId: find?.id,
                              levelId: find.levelId,
                              isLevel: find.isLevel,
                              completed: find?.[completed]
                            })
                        : undefined
                    }
                    onClick={
                      isClickeabled ? () => onSetMarking(lc._id, lc.granularSteps, markingLevel) : undefined
                    }
                    marking={
                      <DetailMarking
                        id={`granular-steps-${levelId}-gs-${item}-lc-${lc._id}`}
                        logs={find?.logs}
                        completed={completed}
                        minMarking={minMarking._id}
                        isBulk={isBulk}
                      />
                    }
                    actionsWorkedExamples={(example) => (
                      <>
                        <IconTooltipButton
                          title='Preview worked example'
                          icon='expand-alt'
                          onClick={(e) => {
                            e.stopPropagation()
                            onWorkedExamples({
                              ulId: lc.ulId,
                              classId: lc.classId,
                              levelId,
                              lcId: lc._id,
                              gsId: find?.id,
                              ...example
                            })
                          }}
                          style={{
                            width: 24,
                            height: 24,
                            color: 'currentColor'
                          }}
                        />
                      </>
                    )}
                    actionsEvidenceOfLearning={(evidence) => (
                      <>
                        <IconTooltipButton
                          title='Preview evidence of learning'
                          icon='expand-alt'
                          onClick={(e) => {
                            e.stopPropagation()
                            onEvidenceOfLearning({
                              ulId: lc.ulId,
                              classId: lc.classId,
                              levelId,
                              lcId: lc._id,
                              gsId: find?.id,
                              ...evidence
                            })
                          }}
                          style={{
                            width: 24,
                            height: 24,
                            color: 'currentColor'
                          }}
                        />
                      </>
                    )}
                    actionsNote={(note) => (
                      <>
                        <IconTooltipButton
                          title='Preview note'
                          icon='expand-alt'
                          onClick={(e) => {
                            e.stopPropagation()
                            onNote({
                              ulId: lc.ulId,
                              classId: lc.classId,
                              levelId,
                              lcId: lc._id,
                              gsId: find?.id,
                              ...note
                            })
                          }}
                          style={{
                            width: 24,
                            height: 24,
                            color: 'currentColor'
                          }}
                        />
                      </>
                    )}
                    actionsTeachingSuggestions={(data) => (
                      <>
                        <IconTooltipButton
                          title='Preview teaching suggestion'
                          icon='expand-alt'
                          onClick={(e) => {
                            e.stopPropagation()
                            onWorkedExamples({
                              ulId: lc.ulId,
                              classId: lc.classId,
                              levelId,
                              levelName: level.name,
                              lcId: lc._id,
                              gsId: find?.id,
                              ...data
                            })
                          }}
                          style={{
                            width: 24,
                            height: 24,
                            color: 'currentColor'
                          }}
                        />
                      </>
                    )}
                  />
                )
              })}
            </div>
          )
        })
        .reverse()
    }
  }

  return []
}

export default CardGranularStep
